import * as B from '@blueprintjs/core';
import {booleanPointInPolygon} from '@turf/turf';
import classnames from 'classnames';
import * as geojson from 'geojson';
import * as I from 'immutable';
import isEqual from 'lodash/isEqual';
import React from 'react';

import AnalyzePolygonChart from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import {NoteGraph} from 'app/components/AnalyzePolygonChart/types';
import {CoordinateDisplay} from 'app/components/CoordinateDisplay';
import {useProjectOverlays} from 'app/components/Library/OverlaysProvider';
import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationUser,
  TagSetting,
  getAreaUnit,
} from 'app/modules/Remote/Organization';
import {checkSelfIntersection} from 'app/pages/ManageProperties/ManagePropertiesDraw';
import CustomizeTagsModal from 'app/pages/MonitorProjectView/CustomizeTagsDialog';
import EditTagsMenu from 'app/pages/MonitorProjectView/EditTagsMenu';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {NewApiNote, NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import * as featureUtils from 'app/utils/featureUtils';
import * as firebaseUtils from 'app/utils/firebaseUtils';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import {toImmutableMap} from 'app/utils/immutableUtils';
import * as layers from 'app/utils/layers';
import * as mapUtils from 'app/utils/mapUtils';
import * as noteUtils from 'app/utils/noteUtils';
import * as tagUtils from 'app/utils/tagUtils';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './NoteCard.module.scss';
import PolygonPlusIcon from './PolygonPlusIcon';
import ReplaceLayerIcon from './ReplaceLayerIcon';
import {
  parseGraphIsoStringsAsDates,
  parseGraphTimeRangeAsIsoStrings,
} from '../AnalyzePolygonChart/utils';
import RerunChartAnalysisTool from '../AnalyzePolygonPopup/RerunChartAnalysisTool';

const MAXIMUM_ATTACHMENT_COUNT = 3;

const NoteForm: React.FunctionComponent<
  React.PropsWithChildren<
    {
      organization: I.ImmutableOf<ApiOrganization>;
      profile: I.ImmutableOf<ApiOrganizationUser>;
      imageRefs: mapUtils.MapImageRefs;

      notesState: NotesState;
      notesActions: NotesActions;

      /** Note exists when viewing a note, or editing an existing note. */
      note?: StateApiNote;

      onClose?: () => void;
      onNoteCreated?: (note: StateApiNote) => void;
      promptForCoordinates?: () => unknown;
      zoomToGeometry?: (geometry: geojson.GeoJSON) => unknown;

      /** Optional flags for disabling note add-ons and actions. */
      autoUpdateLayerAndCursors?: boolean;
      canCollapse?: boolean;
      disableGeometry?: boolean;
      isFocused?: boolean;
      /** Focus the created note so the sidebar scrolls and zooms to it. Defaults to true. */
      focusNoteOnCreate?: boolean;
      /** Starting text for a new note, e.g. a measurement the note is about. */
      initialText?: string;

      // If a graph prop is provided, the user will be able to include the graph
      // data in the note. If a graph prop isn’t provided but the
      // optionally-provided note contains graph data, the user will be able to
      // remove it. The component currently doesn’t reconcile the two if they’re
      // both truthy, but that situation also doesn’t arise in the application.
      graph?: NoteGraph | null;
      showRerunChartAnalysisTool?: boolean;
      renderMinimalForAA?: boolean;
      renderMinimal?: boolean;

      //Optional props we pass in if we need to be able to render the RerunChartAnalysisTool
      featureData?: I.ImmutableListOf<ApiFeatureData> | null;
      firebaseToken?: string;
    } /**if we are rendering the most minimal version of the NoteForm
     *  (ie, just the text field), we can skip a lot of props.
     */ & (
      | {
          renderMinimal: true;
          renderMinimalForAA?: never;
          renderCompact?: never;
          isOverlayVisibleByName?: never;
          onSelectImageRefs?: (imageRefs: mapUtils.MapImageRefs) => unknown | undefined;
          setTags?: LoggedInUserActions['setOrganizationTags'] | undefined;
          featureCollection?: I.ImmutableOf<ApiFeatureCollection> | undefined;
        }
      | {
          renderMinimal?: never;
          renderMinimalForAA?: boolean;
          /** Text, imagery, and location only; for the on-map new-note dialog. */
          renderCompact?: boolean;
          onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => unknown;
          setTags: LoggedInUserActions['setOrganizationTags'];
          featureCollection: I.ImmutableOf<ApiFeatureCollection>;
          isOverlayVisibleByName?: Record<string, boolean>;
        }
    )
  >
> = ({
  organization,
  profile,
  featureCollection,
  imageRefs,
  notesState,
  notesActions,
  note,
  onClose,
  onNoteCreated,
  onSelectImageRefs,
  promptForCoordinates,
  zoomToGeometry,
  setTags,
  autoUpdateLayerAndCursors,
  canCollapse,
  disableGeometry,
  isFocused,
  focusNoteOnCreate = true,
  initialText,
  graph,
  renderMinimalForAA,
  renderMinimal,
  renderCompact,
  showRerunChartAnalysisTool = false,
  firebaseToken,
  featureData,
  isOverlayVisibleByName,
}) => {
  const pendingAttachmentsRef = React.useRef<HTMLInputElement>(null);
  const defaultText = note?.text ?? initialText ?? '';
  const [pendingText, setPendingText] = React.useState(defaultText);

  const defaultImageRefs = note ? note.imageRefs : imageRefs;
  const [pendingImageRefs, setPendingImageRefs] = React.useState(defaultImageRefs);

  const defaultAttachments: (File | string)[] = React.useMemo(
    () => note?.attachments || [],
    [note?.attachments]
  );
  const [pendingAttachments, setPendingAttachments] = React.useState(defaultAttachments);

  const [isPosting, setIsPosting] = React.useState(false);

  const [isExpanded, setIsExpanded] = React.useState(canCollapse ? false : true);

  const defaultGraph = note ? note.graph : graph;
  const [pendingGraph, setPendingGraph] = React.useState(defaultGraph);
  const [includeGraph, setIncludeGraph] = React.useState(true);

  const defaultTagIds = note ? note.tagIds : null;
  const [pendingTagIds, setPendingTagIds] = React.useState(defaultTagIds);

  const defaultOverlayFCIds = note ? (note.overlayFeatureCollectionIds ?? null) : null;
  const [pendingOverlayFCIds, setPendingOverlayFCIds] = React.useState<number[] | null>(
    defaultOverlayFCIds
  );

  const [hasInvalidLayers, setHasInvalidLayers] = React.useState(false);

  React.useEffect(() => {
    setHasInvalidLayers(noteUtils.noteHasInvalidLayersOrCursors(imageRefs));
  }, [imageRefs]);

  // Check if geometry intersects with images whenever geometry or imageRefs change
  const pendingGeometryForValidation = notesState.pendingNoteGeometryFeature?.geometry;
  const hasGeometryIntersectionError = React.useMemo(() => {
    // Only validate if we have all required data
    if (
      !pendingGeometryForValidation ||
      !noteUtils.imageRefsArentBasemap(pendingImageRefs) ||
      !featureData ||
      !featureCollection ||
      renderMinimal
    ) {
      return false;
    }

    const imageRefsWithCursors = pendingImageRefs.filter(
      (ref): ref is mapUtils.MapImageRefWithCursor => ref.cursor !== null
    );

    if (imageRefsWithCursors.length === 0) {
      return false;
    }

    for (const imageRef of imageRefsWithCursors) {
      try {
        const featureDatum = featureUtils.getFeatureDatumFromLayerKey(
          featureData,
          imageRef.layerKey,
          imageRef.cursor,
          featureCollection as unknown as HydratedFeatureCollection
        );
        if (!featureDatum) {
          return true;
        }

        const bounds = featureUtils.getFeatureBoundsforSource(featureDatum, imageRef.layerKey);
        if (!bounds) {
          return true;
        }

        const boundsGeometry = featureUtils.getImageBoundsGeometry(bounds);
        let doesIntersect = false;
        if (pendingGeometryForValidation.type === 'Point') {
          doesIntersect = booleanPointInPolygon(
            pendingGeometryForValidation.coordinates,
            boundsGeometry
          );
        } else if (
          pendingGeometryForValidation.type === 'Polygon' ||
          pendingGeometryForValidation.type === 'MultiPolygon'
        ) {
          const intersection = geoJsonUtils.intersectGeometries(
            pendingGeometryForValidation,
            boundsGeometry
          );
          doesIntersect = !!intersection;
        }

        if (!doesIntersect) {
          return true;
        }
      } catch (error) {
        // If there's an error checking this image, assume it doesn't intersect
        console.warn('Error checking geometry intersection with image:', error);
        return true;
      }
    }
  }, [
    pendingGeometryForValidation,
    pendingImageRefs,
    featureData,
    featureCollection,
    renderMinimal,
  ]);

  const hasGeometrySelfIntersection = React.useMemo(() => {
    if (pendingGeometryForValidation) {
      return checkSelfIntersection(pendingGeometryForValidation);
    }
    return false;
  }, [pendingGeometryForValidation]);

  const pendingImageRefsHaveAnyLayerKey = React.useMemo(
    () => noteUtils.imageRefsHaveAnyLayerKey(pendingImageRefs),
    [pendingImageRefs]
  );

  // Keep local layer information up-to-date with map selection if
  // autoUpdateLayerAndCursors is truthy.
  React.useEffect(() => {
    if (autoUpdateLayerAndCursors) {
      setPendingImageRefs(imageRefs);
    }
  }, [imageRefs, autoUpdateLayerAndCursors]);

  // Keep local graph up-to-date with graph prop if graph prop is truthy.
  React.useEffect(() => {
    if (graph) {
      setPendingGraph(graph);
    }
  }, [graph]);

  // A function to reset form fields to default values.
  const resetFormAndClose = React.useCallback(() => {
    setPendingText(defaultText);
    setPendingImageRefs(defaultImageRefs);
    setPendingGraph(defaultGraph);
    setPendingAttachments(defaultAttachments);
    setPendingOverlayFCIds(defaultOverlayFCIds);
    setIncludeGraph(true);

    if (notesState.pendingNoteGeometryFeature) {
      notesActions.popPendingNoteGeometry(notesState);
    }

    if (pendingAttachmentsRef.current) {
      (pendingAttachmentsRef.current.value as string | null) = null;
    }

    if (onClose) {
      onClose();
    }
  }, [
    defaultAttachments,
    defaultImageRefs,
    defaultGraph,
    defaultText,
    defaultOverlayFCIds,
    notesActions,
    notesState,
    onClose,
  ]);

  // Clear out any pending note geometries on mount. This prevents us from
  // starting the note composition experience unintentionally with a sticky
  // pending geometry from remote state.
  //
  // If the note prop is truthy, we know we’re editing an existing note instead
  // of creating a new one, and therefore want to keep the pending geometry
  // around for potential editing. When the new-note form was opened through
  // the store, startComposingNote has already seeded or cleared the geometry.
  React.useEffect(() => {
    if (!note && !notesState.noteComposer && notesState.pendingNoteGeometryFeature) {
      notesActions.popPendingNoteGeometry(notesState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagIds = tagUtils.getTagIds(
    toImmutableMap(pendingTagIds || {}) as I.ImmutableOf<tagUtils.TagStatusMap>
  );
  // we won't have access to tagSettings if we're rendering the minimal NoteForm
  const tagSettings = !renderMinimal
    ? tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.NOTE)
    : I.OrderedMap<string, I.MapAsRecord<I.ImmutableFields<TagSetting>>>([]);
  const activeTags = tagSettings.filter((t) => tagIds.includes(t!.get('id'))).toList();

  const [isCustomizeTagsModalVisible, setIsCustomizeTagsModalVisible] = React.useState(false);
  const openCustomizeTagsModal = React.useMemo(
    () => (isOwnerOrAdmin(profile.get('role')) ? () => setIsCustomizeTagsModalVisible(true) : null),
    [profile]
  );

  // A function that handles creating a new note.
  const createNote = React.useCallback(
    async (attachmentUrls: string[]) => {
      // Check if the geometry has self-intersections and exclude it if so
      const geometry =
        pendingGeometryForValidation && !hasGeometrySelfIntersection
          ? pendingGeometryForValidation
          : null;
      const update: NewApiNote = {
        text: pendingText,
        imageRefs: pendingImageRefs,
        geometry,
        attachments: attachmentUrls.length ? attachmentUrls : null,
        graph: (includeGraph && pendingGraph) || null,
        tagIds: pendingTagIds,
        overlayFeatureCollectionIds: pendingOverlayFCIds,
      };

      // We set createNote to focus the note so that we’ll automatically scroll
      // to it. The <ScrollToBottom> component would only scroll it in place if
      // we were already scrolled all the way down.
      const newNote = await notesActions.createNote(update, {focus: focusNoteOnCreate});

      if (onNoteCreated) {
        onNoteCreated(newNote);
      }
    },
    [
      pendingGeometryForValidation,
      hasGeometrySelfIntersection,
      pendingText,
      pendingImageRefs,
      includeGraph,
      pendingGraph,
      pendingTagIds,
      pendingOverlayFCIds,
      notesActions,
      onNoteCreated,
      focusNoteOnCreate,
    ]
  );

  // A function that handles updating an existing note.
  const updateNote = async (attachmentUrls: string[]) => {
    // With ! here since we call updateNote conditionally behind a check for a
    // truthy note object.
    const oldNote = note!;

    const update: Partial<StateApiNote> = {};

    if (pendingText !== defaultText) {
      update.text = pendingText;
    }

    // If any part of the raster image reference has changed, update layerKey,
    // cursorKey, and cursorType.
    if (!isEqual(pendingImageRefs, defaultImageRefs)) {
      update.imageRefs = pendingImageRefs;
    }

    if (!isEqual(oldNote.geometry, pendingGeometryForValidation)) {
      if (
        pendingGeometryForValidation &&
        !hasGeometrySelfIntersection &&
        !hasGeometryIntersectionError
      ) {
        update.geometry = pendingGeometryForValidation;
      }
    }

    if (!isEqual(attachmentUrls, defaultAttachments)) {
      update.attachments = attachmentUrls;
    }

    if (!isEqual(oldNote.graph, pendingGraph) || (!!oldNote.graph && !includeGraph)) {
      update.graph = (includeGraph && pendingGraph) || null;
    }

    if (!isEqual(pendingTagIds, defaultTagIds)) {
      update.tagIds = pendingTagIds;
    }

    if (!isEqual(pendingOverlayFCIds, defaultOverlayFCIds)) {
      update.overlayFeatureCollectionIds = pendingOverlayFCIds;
    }

    await notesActions.updateNote(oldNote, update);
  };

  // A function that handles re-saving an existing note with a new graph.
  // For use by RerunChartAnalysisTool.
  const saveReanalyzedNoteGraph = async (newGraph: NoteGraph) => {
    // when we're in this context we are guaranteed to have a note
    const oldNote = note!;

    const update: Partial<StateApiNote> = {};

    update.graph = newGraph;

    setIsPosting(true);

    // TODO: Handle the error case.
    try {
      await notesActions.updateNote(oldNote, update);
      resetFormAndClose();
    } finally {
      setIsPosting(false);
    }
  };

  // A function that handles uploading new attachments to Firebase, setting
  // application state, and delegating the POST or PATCH request, depending on
  // if a note is being created or updated, respectively.
  const onSubmit = async (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();

    if ((!pendingText && !pendingAttachments.length) || isPosting || pendingImageRefsHaveAnyLayerKey) {
      return;
    }

    setIsPosting(true);

    // TODO: Handle the error case.
    try {
      // Upload any new files to Firebase, generating an array of download
      // URLs to save on the note object. See the `uploadAttachment` function
      // documentation for more details about how we create resized versions
      // of note image attachments using a Firebase extension.
      const downloadUrls = await Promise.all(
        pendingAttachments.map(async (file) =>
          file instanceof File ? await notesActions.uploadAttachment(file) : file
        )
      );

      note ? await updateNote(downloadUrls) : await createNote(downloadUrls);

      resetFormAndClose();
    } finally {
      setIsPosting(false);
    }
  };

  // Refocus the text area after editing the pendingNoteGeometry
  const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
  React.useEffect(() => {
    textAreaRef.current?.focus();
  }, [pendingGeometryForValidation]);

  const renderFooter = (small = true) => {
    const createButton = (
      <B.Button
        small={small}
        intent="primary"
        text={note ? (isPosting ? 'Saving…' : 'Save') : isPosting ? 'Creating…' : 'Create'}
        disabled={
          (!pendingText && !pendingAttachments.length) ||
          isPosting ||
          hasInvalidLayers ||
          pendingImageRefsHaveAnyLayerKey ||
          hasGeometrySelfIntersection ||
          hasGeometryIntersectionError
        }
        onClick={onSubmit}
      />
    );

    const showWarning =
      hasInvalidLayers ||
      pendingImageRefsHaveAnyLayerKey ||
      hasGeometryIntersectionError ||
      hasGeometrySelfIntersection;
    const warningContent = hasInvalidLayers ? (
      <div className={cs.popoverContent}>
        <div>To set a note on a basemap layer, exit compare mode.</div>
      </div>
    ) : pendingImageRefsHaveAnyLayerKey ? (
      <div className={cs.popoverContent}>
        <div>Imagery is still loading. Please wait before creating a note.</div>
      </div>
    ) : hasGeometryIntersectionError ? (
      <div className={cs.popoverContent}>
        <div>The note location must intersect with all attached images.</div>
      </div>
    ) : hasGeometrySelfIntersection ? (
      <div className={cs.popoverContent}>
        <div>The note geometry cannot have self-intersections. </div>
      </div>
    ) : undefined;

    return (
      <div className={classnames(cs.footer, cs.formFooter)}>
        {showWarning ? (
          <B.Popover
            interactionKind="hover"
            modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
            content={warningContent}
          >
            {createButton}
          </B.Popover>
        ) : (
          <>{createButton}</>
        )}

        <B.Button
          small={small}
          minimal
          text="Cancel"
          onClick={() => {
            resetFormAndClose();

            canCollapse && setIsExpanded(false);
          }}
        />
      </div>
    );
  };

  const renderTextArea = () => (
    <B.TextArea
      inputRef={(el) => (textAreaRef.current = el)}
      autoFocus={!canCollapse}
      fill
      rows={isExpanded ? 3 : 1}
      className={cs.formTextArea}
      placeholder="Add a note"
      onFocus={() => setIsExpanded(true)}
      onChange={(e) => setPendingText(e.currentTarget.value)}
      value={pendingText}
      growVertically
    />
  );

  const addNoteGeometryText = React.useMemo(() => {
    let helperText = '';

    if (notesState.addPendingNoteGeometryMode === 'selectOverlay') {
      // Scenario 1: Selecting from an existing overlay
      helperText =
        notesState.overlayGeometryError ||
        'Click on an overlay polygon or point to use its geometry. Enable overlays from the Change Overlays menu or the Lens Library.';
    } else if (notesState.addPendingNoteGeometryMode === 'addPolygon') {
      // Scenario 2: Adding another polygon to an existing geometry
      helperText =
        'Click on the map to draw an additional polygon area. Double-click to complete it.';
    } else if (notesState.pendingNoteGeometryFeature) {
      // Scenario 3: Updating an existing geometry
      if (notesState.addPendingNoteGeometryMode === 'point') {
        helperText = 'Click on the map to update the point location.';
      } else {
        const shape = notesState.addPendingNoteGeometryMode === 'rect' ? 'rectangle' : 'polygon';
        helperText = `Click and drag the ${shape} to update the area location.`;
      }
    } else {
      // Scenario 4: Creating a new geometry from scratch
      if (notesState.addPendingNoteGeometryMode === 'point') {
        helperText = 'Click on the map to add a point location.';
      } else {
        const isPolygon = notesState.addPendingNoteGeometryMode === 'polygon';
        const completionHint = isPolygon ? ' Double-click to complete it.' : '';
        helperText = `Click on the map to start adding an area location.${completionHint}`;
      }
    }

    return helperText;
  }, [notesState]);

  const renderImageryAddOn = () => (
    <div className={cs.formAddOn}>
      <CircleIcon icon="media" />

      {noteUtils.imageRefsArentBasemap(pendingImageRefs) ? (
        <div onClick={() => onSelectImageRefs?.(pendingImageRefs)}>
          {noteUtils.formatImageRefs(pendingImageRefs)}
        </div>
      ) : (
        <div className={cs.placeholderText}>No imagery</div>
      )}

      {!autoUpdateLayerAndCursors && (
        <B.Button
          small
          minimal
          icon={<ReplaceLayerIcon />}
          title="Update note image to match map"
          disabled={isEqual(imageRefs, pendingImageRefs)}
          onClick={() => {
            setPendingImageRefs(imageRefs);
          }}
        />
      )}
      {noteUtils.imageRefsArentBasemap(pendingImageRefs) ? (
        <B.Popover
          interactionKind="hover"
          // Allows popper.js to position the popover outside of the dialog’s
          // bounds. The `hide: {enabled: false}` bit prevents popper.js from
          // spamming the console with warnings.
          modifiers={{preventOverflow: {enabled: false}, hide: {enabled: false}}}
          content={<div className={cs.popoverContent}>Remove layers and dates</div>}
        >
          <B.Button
            icon="cross"
            minimal
            small
            onClick={() => {
              // We want to do slightly different things here if editing a note vs creating a new one
              const basemapImageRef: mapUtils.MapImageRefs = [
                {layerKey: layers.NONE, cursor: null},
              ];
              if (!autoUpdateLayerAndCursors) {
                setPendingImageRefs(basemapImageRef);
              } else {
                onSelectImageRefs?.(basemapImageRef);
              }
            }}
          />
        </B.Popover>
      ) : null}
    </div>
  );

  const renderGeometryAddOns = () => (
    <>
      {!disableGeometry && (
        <div className={cs.formAddOn}>
          <CircleIcon icon="geolocation" />

          {pendingGeometryForValidation ? (
            <div
              className={classnames({[cs.pointer]: zoomToGeometry})}
              onClick={() => zoomToGeometry && zoomToGeometry(pendingGeometryForValidation)}
            >
              <CoordinateDisplay geometry={pendingGeometryForValidation} />{' '}
              {(pendingGeometryForValidation.type === 'Polygon' ||
                pendingGeometryForValidation.type === 'MultiPolygon') && (
                <>
                  (
                  {geoJsonUtils.makeAreaDisplayString(
                    pendingGeometryForValidation,
                    getAreaUnit(organization)
                  )}
                  )
                </>
              )}
            </div>
          ) : (
            <div className={cs.placeholderText}>No location</div>
          )}

          {pendingGeometryForValidation ? (
            <>
              {(pendingGeometryForValidation.type === 'Polygon' ||
                pendingGeometryForValidation.type === 'MultiPolygon') && (
                <B.Button
                  small
                  minimal
                  icon={<PolygonPlusIcon />}
                  title="Add another polygon"
                  active={notesState.addPendingNoteGeometryMode === 'addPolygon'}
                  onClick={() => notesActions.startAddingPolygonToPendingGeometry()}
                />
              )}
              <B.Tooltip
                disabled={!note?.graph || !pendingGraph}
                content={'Cannot remove location from a note with a graph.'}
              >
                <B.Button
                  small
                  minimal
                  disabled={!!note?.graph && !!pendingGraph}
                  icon="trash"
                  title="Remove location"
                  onClick={() => notesActions.popPendingNoteGeometry(notesState)}
                />
              </B.Tooltip>
            </>
          ) : (
            <B.ButtonGroup>
              <B.Button
                small
                minimal
                icon="polygon-filter"
                title="Add map area"
                active={notesState.addPendingNoteGeometryMode === 'polygon'}
                onClick={() => notesActions.startNewPendingGeometry('polygon')}
              />
              <B.Button
                small
                minimal
                icon="widget"
                title="Add rectangle map area"
                active={notesState.addPendingNoteGeometryMode === 'rect'}
                onClick={() => notesActions.startNewPendingGeometry('rect')}
              />
              <B.Button
                small
                minimal
                icon="map-marker"
                title="Add map point"
                active={notesState.addPendingNoteGeometryMode === 'point'}
                onClick={() => notesActions.startNewPendingGeometry('point')}
              />
              <B.Button
                small
                minimal
                icon="layers"
                title="Select from overlay"
                active={notesState.addPendingNoteGeometryMode === 'selectOverlay'}
                onClick={() => notesActions.startNewPendingGeometry('selectOverlay')}
              />
              <B.Button
                small
                minimal
                icon="numerical"
                title="Enter coordinates manually"
                onClick={promptForCoordinates}
              />
            </B.ButtonGroup>
          )}
        </div>
      )}

      {!disableGeometry && !!notesState.addPendingNoteGeometryMode && (
        <div className={cs.formAddOn}>
          {/** Placeholder so the callout is flush with the location text. */}
          <CircleIcon icon={null} />

          <B.Callout className={cs.formCallout} intent="warning" icon={null}>
            {addNoteGeometryText}
          </B.Callout>
        </div>
      )}
    </>
  );

  const renderAttachmentsAddOns = () => (
    <>
      <div className={cs.formAddOn}>
        <CircleIcon icon="paperclip" />

        {pendingAttachments.length ? (
          <div>
            {pendingAttachments.length}{' '}
            {pendingAttachments.length > 1 ? 'attachments' : 'attachment'}
          </div>
        ) : (
          <div className={cs.placeholderText}>No attachments</div>
        )}

        <B.Button
          small
          minimal
          icon="plus"
          title={`Add up to ${MAXIMUM_ATTACHMENT_COUNT} attachments`}
          disabled={pendingAttachments.length >= MAXIMUM_ATTACHMENT_COUNT}
          onClick={() => pendingAttachmentsRef.current?.click()}
        />
      </div>

      {!!pendingAttachments.length && (
        <div className={cs.formAddOn}>
          <CircleIcon icon={null} />

          <div className={cs.formAttachments}>
            {pendingAttachments.map((attachment, i) => (
              <B.Tag
                key={i}
                round
                minimal
                // TODO: We should also delete files from Google Cloud
                // Storage when they are removed from notes.
                onRemove={() =>
                  setPendingAttachments((prevPendingAttachments) =>
                    prevPendingAttachments.filter((_, idx) => idx !== i)
                  )
                }
              >
                {attachment instanceof File
                  ? attachment.name
                  : firebaseUtils.getFileNameFromObjectDownloadUrl(attachment)}
              </B.Tag>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderTagsAddOn = () => (
    <div className={cs.formAddOn}>
      <CircleIcon icon="tag" />

      {activeTags.size ? (
        <div>
          <>
            {activeTags
              .map((tag) => <tagUtils.Tag key={tag!.get('id')} setting={tag!} />)
              .toArray()}
          </>
        </div>
      ) : (
        <div className={cs.placeholderText}>No Note Tags</div>
      )}

      {featureCollection &&
        (isOwnerOrAdmin(profile.get('role')) || profile.get('role') === 'regular') && (
        <EditTagsMenu
          featureCollectionId={featureCollection.get('id')}
          tagSettings={tagSettings}
          tagStatusMaps={
            I.List([toImmutableMap(pendingTagIds || {})]) as I.ImmutableOf<tagUtils.TagStatusMap[]>
          }
          onChange={(newTagIds) => {
            // newTagIds only contains the newly selected/deselected tagIds
            // so we merge it with the currently selected tagIds
            setPendingTagIds({...pendingTagIds, ...newTagIds});
          }}
          openCustomizeTagsModal={openCustomizeTagsModal}
          arrow
          kind={tagUtils.TAG_KIND.NOTE}
        >
          <B.Button icon={<B.Icon icon="more" />} minimal={true} small title="Edit Note Tags" />
        </EditTagsMenu>
      )}
      {isCustomizeTagsModalVisible && !renderMinimal && (
        <CustomizeTagsModal
          organization={organization}
          setTags={setTags}
          onClose={() => setIsCustomizeTagsModalVisible(false)}
          kind={tagUtils.TAG_KIND.NOTE}
        />
      )}
    </div>
  );

  /**
   * Every add-on the note form offers. The compact dialog on the map shows the
   * same set as the sidebar, so a note started from either place can carry the
   * same things.
   */
  const renderAddOns = () => (
    <div className={cs.formAddOns}>
      {renderImageryAddOn()}
      {renderGeometryAddOns()}
      {renderAttachmentsAddOns()}
      {renderTagsAddOn()}
      {featureCollection && (
        <OverlayNoteAddOn
          featureCollection={featureCollection}
          isNewNote={!note}
          isOverlayVisibleByName={isOverlayVisibleByName}
          pendingOverlayFCIds={pendingOverlayFCIds}
          onChangePendingOverlayFCIds={setPendingOverlayFCIds}
        />
      )}
    </div>
  );

  if (renderMinimalForAA && !!graph) {
    return (
      <AnalysisNoteForm
        pendingImageRefs={pendingImageRefs}
        includeGraph={includeGraph}
        onSelectImageRefs={onSelectImageRefs}
        setIncludeGraph={setIncludeGraph}
        renderTextArea={renderTextArea}
        renderFooter={renderFooter}
        featureCollection={featureCollection}
        isOverlayVisibleByName={isOverlayVisibleByName}
        pendingOverlayFCIds={pendingOverlayFCIds}
        onChangePendingOverlayFCIds={setPendingOverlayFCIds}
      />
    );
  }

  if (renderMinimal) {
    return <MinimalNoteForm renderTextArea={renderTextArea} renderFooter={renderFooter} />;
  }

  if (renderCompact) {
    return (
      <div className={cs.compact}>
        <HiddenFileInput
          pendingAttachmentsRef={pendingAttachmentsRef}
          pendingAttachments={pendingAttachments}
          setPendingAttachments={setPendingAttachments}
        />
        <div className={cs.form}>
          {renderTextArea()}
          {renderAddOns()}
        </div>
        {renderFooter()}
      </div>
    );
  }

  return (
    <div className={classnames(cs.card, {[cs.focusedNote]: isFocused})}>
      <HiddenFileInput
        pendingAttachmentsRef={pendingAttachmentsRef}
        pendingAttachments={pendingAttachments}
        setPendingAttachments={setPendingAttachments}
      />

      <div className={cs.form}>
        {renderTextArea()}

        {/**aggressive null checking on this internal tool-- we are guaranteed to have all of these
         * features when generating this chart, since it is built from a chart saved to a note, but
         * we null check out here in case something is taking a bit to load async and to prevent a
         * ton of null checking in the feature itself.
         */}
        {note && !!note.graph && showRerunChartAnalysisTool && !!featureData && firebaseToken && (
          <RerunChartAnalysisTool
            isPosting={isPosting}
            organization={organization}
            featureData={featureData}
            firebaseToken={firebaseToken}
            note={note}
            saveReanalyzedNoteGraph={saveReanalyzedNoteGraph}
          />
        )}

        {!!note?.graph && !!pendingGraph && (
          <AnalyzePolygonChart
            graph={parseGraphIsoStringsAsDates(note.graph)}
            imageRefs={note.imageRefs}
            areaInM2={noteUtils.getNoteAreaInM2(note)}
            areaUnit={getAreaUnit(organization)}
            onDataRangeModeChange={(nextMode) => {
              setPendingGraph((prevPendingGraph) => {
                if (prevPendingGraph) {
                  const previousMode = prevPendingGraph.graphDataRangeMode || 'none';
                  if (!isEqual(previousMode, nextMode)) {
                    return {...prevPendingGraph, graphDataRangeMode: nextMode};
                  } else {
                    return prevPendingGraph;
                  }
                }
              });
            }}
            onDataRangeChange={(dataRange) => {
              setPendingGraph((prevPendingGraph) => {
                if (prevPendingGraph) {
                  const prevPendingGraphDataRange = prevPendingGraph.graphDataRange;
                  const nextPendingGraphDataRange = dataRange;
                  if (!isEqual(prevPendingGraphDataRange, nextPendingGraphDataRange)) {
                    return {...prevPendingGraph, graphDataRange: nextPendingGraphDataRange};
                  } else {
                    return prevPendingGraph;
                  }
                } else {
                  return prevPendingGraph;
                }
              });
            }}
            onTimeRangeChange={(timeRange) => {
              /** Update the pending graph in state with the new time range
               * formatted as ISO strings, if different. */
              setPendingGraph((prevPendingGraph) => {
                if (prevPendingGraph) {
                  const prevPendingGraphTimeRange = prevPendingGraph.graphTimeRange;
                  const nextPendingGraphTimeRange = parseGraphTimeRangeAsIsoStrings(timeRange);
                  if (!isEqual(prevPendingGraphTimeRange, nextPendingGraphTimeRange)) {
                    return {...prevPendingGraph, graphTimeRange: nextPendingGraphTimeRange};
                  } else {
                    return prevPendingGraph;
                  }
                } else {
                  return prevPendingGraph;
                }
              });
            }}
            onColumnsChange={(dataColumns) => {
              setPendingGraph((prevPendingGraph) => {
                if (prevPendingGraph) {
                  return {...prevPendingGraph, dataColumns: dataColumns};
                }
              });
            }}
            hideLegend={false}
            isLegendInteractive={true}
            controls={[
              <B.Button
                key="remove-graph-btn"
                icon="cross"
                onClick={() => {
                  if (
                    confirm(
                      'Are you sure you want to remove this graph? Graphs cannot be re-added to notes.'
                    )
                  ) {
                    setPendingGraph(null);
                  }
                }}
                small
                title="Remove graph"
              />,
            ]}
            graphStyle={{height: 125}}
            isExpanded={false}
          />
        )}

        {isExpanded && renderAddOns()}
      </div>

      {isExpanded && renderFooter()}
    </div>
  );
};

/**
 * Renders a hidden multifile input that validates the number of selected files
 * against the maximum count and updates `pendingAttachments` state.
 */
const HiddenFileInput: React.FunctionComponent<
  React.PropsWithChildren<{
    pendingAttachmentsRef: React.RefObject<HTMLInputElement>;
    pendingAttachments: (File | string)[];
    setPendingAttachments: React.Dispatch<React.SetStateAction<(string | File)[]>>;
  }>
> = ({pendingAttachmentsRef, pendingAttachments, setPendingAttachments}) => (
  <input
    accept="image/*, .pdf, .csv"
    style={{display: 'none'}}
    id="file"
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = e.currentTarget.files;

      if (newFiles) {
        const newFilesArr = Array.from(newFiles);

        if ([...pendingAttachments, ...newFilesArr].length > MAXIMUM_ATTACHMENT_COUNT) {
          return alert(
            `You can only attach a maximum of ${MAXIMUM_ATTACHMENT_COUNT} files to a single note.`
          );
        }

        setPendingAttachments((prevPendingAttachments) => [
          ...prevPendingAttachments,
          ...newFilesArr,
        ]);
      }
    }}
    ref={pendingAttachmentsRef}
    // Passing in an empty string as the value allows us to re-select the same
    // file, triggering the onChange event each time.
    value=""
    type="file"
    multiple
  />
);

/**
 * Renders a stylized Blueprint icon with a light blue background and bright
 * blue SVG fill.
 */
const CircleIcon: React.FunctionComponent<
  React.PropsWithChildren<{
    icon: B.IconName | B.MaybeElement;
  }>
> = ({icon}) => {
  // If we aren't rendering a icon, make the background completely transparent.
  // This allow us to add blank placeholders on lines that don't have an icon
  // (e.g., danger callout when layer is unchecked).
  const backgroundColor = icon ? undefined : 'transparent';

  return (
    <div
      className={cs.circleIcon}
      style={{height: 24, width: 24, borderRadius: 12, backgroundColor}}
    >
      <B.Icon intent="primary" icon={icon} iconSize={12} />
    </div>
  );
};

export default NoteForm;

/**
 * Overlay section for the note form. Rendered as a separate component so
 * useProjectOverlays can be called unconditionally.
 */
const OverlayNoteAddOn: React.FC<{
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  isNewNote: boolean;
  isOverlayVisibleByName?: Record<string, boolean>;
  pendingOverlayFCIds: number[] | null;
  onChangePendingOverlayFCIds: (ids: number[] | null) => void;
}> = ({
  featureCollection,
  isNewNote,
  isOverlayVisibleByName,
  pendingOverlayFCIds,
  onChangePendingOverlayFCIds,
}) => {
  const {projectOverlays, overlaySettings} = useProjectOverlays(featureCollection);

  // When creating a new note, pre-populate with currently visible overlays.
  const initRef = React.useRef(false);
  React.useEffect(() => {
    if (isNewNote && !initRef.current && projectOverlays.length > 0) {
      initRef.current = true;
      const visibleIds = projectOverlays
        .filter((o) =>
          isOverlayVisibleByName
            ? (isOverlayVisibleByName[o.name] ?? false)
            : (overlaySettings.get(o.name)?.defaultEnabled ?? false)
        )
        .map((o) => o.id);
      onChangePendingOverlayFCIds(visibleIds.length > 0 ? visibleIds : null);
    }
    /*
      This effect is intentionally a one-time initialization — it pre-populates overlays when creating a new note.
      The initRef guards against it running more than once.
      The suppressed dependencies are isOverlayVisibleByName, overlaySettings, and onChangePendingOverlayFCIds.
      If those were included, the effect could re-run and overwrite the user's overlay selection whenever those values changed.
      The only legitimate re-trigger is projectOverlays (included) — specifically waiting for it to be non-empty on first load.
      Once initRef.current = true, the guard prevents any repeat.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectOverlays]);

  if (projectOverlays.length === 0) {
    return null;
  }

  const selectedIdSet = new Set(pendingOverlayFCIds ?? []);
  const selectedOverlays = projectOverlays
    .filter((o) => selectedIdSet.has(o.id))
    .sort((a, b) => featureUtils.alphanumericSort(a.name, b.name));

  return (
    <div className={cs.formAddOn}>
      <CircleIcon icon="layers" />

      {selectedOverlays.length > 0 ? (
        <div className={cs.overlayTags}>
          {selectedOverlays.map((o) => (
            <B.Tag key={o.id} minimal round>
              {o.name}
            </B.Tag>
          ))}
        </div>
      ) : (
        <div className={cs.placeholderText}>No overlays</div>
      )}

      <B.Popover
        position="bottom"
        minimal
        content={
          <B.Menu>
            <B.MenuItem
              text="Clear all overlays"
              disabled={selectedIdSet.size === 0}
              shouldDismissPopover={false}
              onClick={() => onChangePendingOverlayFCIds(null)}
            />
            <B.MenuDivider />
            {[...projectOverlays]
              .sort((a, b) => featureUtils.alphanumericSort(a.name, b.name))
              .map((overlay) => (
                <B.MenuItem
                  key={overlay.id}
                  text={overlay.name}
                  icon={selectedIdSet.has(overlay.id) ? 'tick' : 'blank'}
                  shouldDismissPopover={false}
                  onClick={() => {
                    const next = new Set(selectedIdSet);
                    if (next.has(overlay.id)) {
                      next.delete(overlay.id);
                    } else {
                      next.add(overlay.id);
                    }
                    const arr = [...next];
                    onChangePendingOverlayFCIds(arr.length > 0 ? arr : null);
                  }}
                />
              ))}
          </B.Menu>
        }
      >
        <B.Button icon={<B.Icon icon="more" />} minimal small title="Edit overlays" />
      </B.Popover>
    </div>
  );
};

const AnalysisNoteForm: React.FC<{
  pendingImageRefs: mapUtils.MapImageRefs;
  includeGraph: boolean;
  setIncludeGraph: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectImageRefs: (imageRefs: mapUtils.MapImageRefs) => unknown;
  renderTextArea: () => React.JSX.Element;
  renderFooter: (boolean) => React.JSX.Element;
  featureCollection?: I.ImmutableOf<ApiFeatureCollection>;
  isOverlayVisibleByName?: Record<string, boolean>;
  pendingOverlayFCIds: number[] | null;
  onChangePendingOverlayFCIds: (ids: number[] | null) => void;
}> = ({
  pendingImageRefs,
  includeGraph,
  onSelectImageRefs,
  setIncludeGraph,
  renderTextArea,
  renderFooter,
  featureCollection,
  isOverlayVisibleByName,
  pendingOverlayFCIds,
  onChangePendingOverlayFCIds,
}) => {
  return (
    <div className={cs.minimalAa}>
      <div className={cs.form}>
        {renderTextArea()}
        <div className={cs.formAddOns}>
          <div className={cs.formAddOn}>
            <CircleIcon icon="media" />

            {noteUtils.imageRefsArentBasemap(pendingImageRefs) ? (
              <div onClick={() => onSelectImageRefs(pendingImageRefs)}>
                {noteUtils.formatImageRefs(pendingImageRefs)}
              </div>
            ) : (
              <div className={cs.placeholderText}>No imagery</div>
            )}
          </div>
          {featureCollection && (
            <OverlayNoteAddOn
              featureCollection={featureCollection}
              isNewNote={true} // AnalysisNoteForm is only used for new notes in the AA flow
              isOverlayVisibleByName={isOverlayVisibleByName}
              pendingOverlayFCIds={pendingOverlayFCIds}
              onChangePendingOverlayFCIds={onChangePendingOverlayFCIds}
            />
          )}
          <div
            className={cs.formAddOn}
            style={{cursor: 'pointer', userSelect: 'none'}}
            onClick={() => setIncludeGraph((prev) => !prev)}
          >
            <B.Checkbox
              className={cs.formAddOnCheckbox}
              checked={includeGraph}
              onChange={() => setIncludeGraph((prev) => !prev)}
            />

            <div>Include chart in note</div>
          </div>
        </div>
      </div>
      {renderFooter(false)}
    </div>
  );
};

/** A version of NoteForm that renders just the text input and submit/cancel buttons. */
const MinimalNoteForm: React.FC<{
  renderTextArea: () => React.JSX.Element;
  renderFooter: (boolean) => React.JSX.Element;
}> = ({renderTextArea, renderFooter}) => {
  return (
    <div className={cs.minimal}>
      <div>{renderTextArea()}</div>
      <div className={cs.footer}>{renderFooter(false)}</div>
    </div>
  );
};
